import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import ReactMarkdown from 'react-markdown';

interface LegalPageLayoutProps {
  title: string;
  markdown: string;
}

const backFromMap: Record<string, { to: string; label: string }> = {
  login: { to: '/login', label: 'Back to Login' },
  signup: { to: '/signup', label: 'Back to Signup' },
};

const LegalPageLayout: React.FC<LegalPageLayoutProps> = ({ title, markdown }) => {
  const [searchParams] = useSearchParams();
  const from = searchParams.get('from');
  const back = from && backFromMap[from] ? backFromMap[from] : { to: '/', label: 'Back to Home' };

  return (
    <div className="min-h-screen bg-gray-900">
      <div className="relative overflow-hidden bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white min-h-screen">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -right-64 -top-64 w-[800px] h-[800px] bg-gradient-to-r from-cyan-500/5 to-blue-500/5 rounded-full" />
          <div className="absolute -left-64 -bottom-64 w-[800px] h-[800px] bg-gradient-to-r from-purple-500/5 to-pink-500/5 rounded-full" />
        </div>
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
          <Link
            to={back.to}
            className="inline-flex items-center text-sm text-gray-300 hover:text-white mb-6 sm:mb-8 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2 focus:ring-offset-gray-900 rounded"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-1" aria-hidden />
            {back.label}
          </Link>
          <article
            className="legal-content prose prose-invert prose-headings:text-white prose-headings:font-semibold prose-p:text-gray-300 prose-li:text-gray-300 prose-strong:text-white prose-a:text-cyan-400 prose-a:no-underline hover:prose-a:underline prose-hr:border-white/10 max-w-none"
            aria-label={title}
          >
            <ReactMarkdown
              components={{
                h1: ({ node, children, ...props }) => <h1 className="text-2xl sm:text-3xl font-bold text-white mb-6 mt-0" {...props}>{children}</h1>,
                h2: ({ node, children, ...props }) => <h2 className="text-lg sm:text-xl font-semibold text-white mt-8 mb-3 border-b border-white/10 pb-2" {...props}>{children}</h2>,
                p: ({ node, ...props }) => <p className="text-gray-300 mb-4 leading-relaxed" {...props} />,
                ul: ({ node, ...props }) => <ul className="list-disc pl-6 mb-4 space-y-1 text-gray-300" {...props} />,
                li: ({ node, ...props }) => <li className="leading-relaxed" {...props} />,
                hr: ({ node, ...props }) => <hr className="border-white/10 my-6" {...props} />,
                a: ({ node, href, children, ...props }) => (
                  <a href={href} target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:text-cyan-300 underline" {...props}>{children}</a>
                ),
              }}
            >
              {markdown}
            </ReactMarkdown>
          </article>
          <div className="mt-10 pt-6 border-t border-white/10">
            <Link
              to={back.to}
              className="inline-flex items-center text-sm text-cyan-400 hover:text-cyan-300 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2 focus:ring-offset-gray-900 rounded"
            >
              <ArrowLeftIcon className="h-4 w-4 mr-1" aria-hidden />
              {back.label}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LegalPageLayout;
